import { zodResolver } from '@hookform/resolvers/zod'
import { Appointment } from '@myapp/contract'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { appointmentApi } from '../../../../entities/appointment/api/service'
import { appointmentKeys } from '../../../../entities/appointment/api/appointment.keys'
import { DurationSpinner } from '../../../../shared/ui/controls/DurationSpinner/DurationSpinner'
import Modal from '../../../../shared/ui/overlay/Modal/Modal'
import { departureReminderFormSchema, DepartureReminderFormValues } from '../model/departureReminderForm.schema'

interface Props {
  show: boolean
  setShow: (show: boolean) => void
  appointment: Appointment
  onSuccess: () => void
}

export function AppointmentDepartureReminderModal({
  show,
  setShow,
  appointment,
  onSuccess,
}: Props) {
  const queryClient = useQueryClient()

  const saveAbfahrt = useMutation({
    mutationFn: (dto: DepartureReminderFormValues) =>
      appointmentApi.update(appointment.id, { actualDepartureDuration: dto.actualDepartureDuration }).then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all })
    },
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<DepartureReminderFormValues>({
    resolver: zodResolver(departureReminderFormSchema),
    defaultValues: {
      actualDepartureDuration: appointment.plannedDepartureDuration,
    },
  })

  useEffect(() => {
    if (show) {
      reset({ actualDepartureDuration: appointment.plannedDepartureDuration })
    }
  }, [show, appointment.plannedDepartureDuration, reset])

  const onSubmit = (values: DepartureReminderFormValues) => {
    saveAbfahrt.mutate(values, { onSuccess })
  }

  return (
    <Modal
      show={show}
      setShow={setShow}
      title="Abfahrt nachtragen"
      submitButton={{ onSubmit: handleSubmit(onSubmit), title: 'Speichern', isPending: saveAbfahrt.isPending }}
    >
      <div className="flex flex-col gap-4 w-full">
        <Controller
          name="actualDepartureDuration"
          control={control}
          render={({ field }) => (
            <DurationSpinner
              label="Abfahrt (min)"
              value={field.value}
              onChange={field.onChange}
              error={errors.actualDepartureDuration?.message}
            />
          )}
        />
      </div>
    </Modal>
  )
}
